import * as React from 'react';
import { Input, Box, createStyles } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useTranslation } from 'react-i18next';
import { useId } from '@mantine/hooks';

// Components
import { InputValueTable } from '../Form/components/InputValueTable';
import { ActionButtons } from '../Form/components/ActionButtons';

// Helpers
import { hydrateManifestSection } from '../Form/helpers/hydrateManifestSection';

// Hooks
import { useStaticKey } from '../Form/hooks/useStaticKey';

// Styles
import { getCommonFieldStyles } from '../Form/helpers/fieldStyles';

// Types
import { GenericFieldProps } from '../Form/types';
import { SummationLineItem, ROW_TYPES } from '../../types';
import { IManifestFieldHydrated, IManifestField, FieldProps } from '../../manifests/types';

const useStyles = createStyles((theme) => {
    const commonFieldStyles = getCommonFieldStyles(theme);

    return {
        root: commonFieldStyles.root,
        input: commonFieldStyles.input,
        inputHighlight: commonFieldStyles.inputHighlight,
        labelHighlight: commonFieldStyles.labelHighlight,
        readonlyInput: commonFieldStyles.readonlyInput,
        required: commonFieldStyles.required,
        hiddenDisplay: commonFieldStyles.hiddenDisplay,
        error: commonFieldStyles.error,
        wrapper: commonFieldStyles.wrapper,
        readonlyWrapper: commonFieldStyles.readonlyWrapper,
        description: commonFieldStyles.description,
        label: {
            ...commonFieldStyles.label,
            top: '1px',
            left: '1px',
            right: '21px',
            borderRadius: '4px',
            background: '#fff'
        }
    };
});

const ACTIONS = {
    MINUS: 'minus',
    PLUS: 'plus'
};
interface CustomFinancialsTableProps<FormValues> {
    data?: any[];
    subManifest: IManifestFieldHydrated[] | IManifestField[];
    parentform: UseFormReturnType<FormValues>;
    parentNext?: () => Promise<void>;
    parentPrev?: () => void;
    validator?: (
        val?: any,
        values?: Record<string, any>,
        requiredValues?: string[]
    ) => Record<string, string | null> | string | null;
    activeStep?: number;
    surveyId?: string;
    clientId?: string;
    trigger?: string;
    componentProps?: Partial<FieldProps>;
    getSideEffectUpdates?: (values: Record<string, any>) => Record<string, any>;
}

export function FinancialsTable<FormValues extends Record<string, any>>(props: CustomFinancialsTableProps<FormValues>) {
    const { classes, cx } = useStyles();
    const { getStaticKeyByName, getNameByStaticKey, convertMapFromNamesToKeys, convertMapFromKeysToNames } =
        useStaticKey();
    const { subManifest, parentform } = props;
    const tableId = useId();
    const [errors, setErrors] = React.useState<Record<string, string | null> | string | null>(null);
    const [hasRunValidation, setHasRunValidation] = React.useState(false);
    const { t } = useTranslation('questions');
    const { t: commonTranslation } = useTranslation();
    const manifest = React.useMemo(() => hydrateManifestSection(subManifest), [subManifest, hydrateManifestSection]);
    const tableData = React.useMemo(() => {
        return (manifest ?? []).map((fieldConfig) => {
            return {
                value: parentform.values?.[fieldConfig?.key],
                required: fieldConfig?.componentProps?.required ?? false,
                key: fieldConfig?.key,
                id: fieldConfig?.id,
                label: fieldConfig?.componentProps?.labelKey
                    ? t(fieldConfig?.componentProps?.labelKey)
                    : 'Label Undefined',
                displayAsNegative: fieldConfig?.componentProps?.displayAsNegative ?? false,
                type: fieldConfig?.componentProps?.isTotal
                    ? ROW_TYPES.TOTAL
                    : fieldConfig?.componentProps?.computed
                    ? ROW_TYPES.SUM_OF
                    : ROW_TYPES.AMOUNT,
                hidden: fieldConfig?.componentProps?.hidden,
                fieldConfig
            };
        }) as SummationLineItem[];
    }, [manifest, parentform]);
    const calculateSum = React.useCallback(
        (values: Partial<FormValues>, sumOf: string[]) => {
            const sumOfNames = sumOf.map((staticKey: string) => {
                const action = staticKey[0] === '-' ? ACTIONS.MINUS : ACTIONS.PLUS;

                return {
                    action: action,
                    name: getNameByStaticKey(action === ACTIONS.MINUS ? staticKey.substring(1) : staticKey)
                };
            });

            return sumOfNames.reduce((acc, curr) => {
                if (curr.action === ACTIONS.MINUS) {
                    return acc - (values?.[curr.name] ?? 0);
                } else {
                    return typeof values?.[curr.name] !== 'undefined' ? acc + (values?.[curr.name] ?? 0) : acc;
                }
            }, 0);
        },
        [getNameByStaticKey]
    );
    const updateValue = React.useCallback(
        ({ newValue, key }: { newValue?: any; key: string }) => {
            const newState = {
                ...parentform.values,
                [key]: newValue
            };
            const computedFields = tableData
                .filter(({ fieldConfig }) => {
                    return (
                        fieldConfig?.alwaysCompute ||
                        (fieldConfig?.componentProps?.sumOf ?? [])
                            .map((key: string) => (key.startsWith('-') ? key.substring(1) : key))
                            .includes(getStaticKeyByName(key))
                    );
                })
                .reduce((acc, curr) => {
                    return {
                        ...acc,
                        [curr.key]: calculateSum(acc, curr?.fieldConfig?.componentProps?.sumOf ?? [])
                    };
                }, newState);

            const baseUpdatedState = {
                ...newState,
                ...computedFields
            } as Partial<FormValues>;
            let sideEffectUpdates = {};

            const updatedState = {
                ...baseUpdatedState,
                ...sideEffectUpdates
            };
            console.log('updatedState', updatedState);
            parentform.setValues(updatedState);
            parentform?.setDirty(
                Object.keys(updatedState).reduce(
                    (acc, curr) => ({
                        [curr]: true
                    }),
                    {}
                )
            );
        },
        [parentform?.values, tableData]
    );
    const valuesByName = React.useMemo(
        () => (subManifest ?? []).map(({ id }) => (id ? getNameByStaticKey(id) : null)).filter(Boolean),
        [subManifest]
    );
    const valuesToValidate = React.useMemo(() => {
        return Object.entries(parentform.values).reduce((acc, [key, val]) => {
            // Filter only values in this form section and only those which are not computed values
            return valuesByName.includes(key) &&
                !(manifest.find((field) => field.key === key)?.componentProps?.computed === true)
                ? { ...acc, [key]: val }
                : acc;
        }, {});
    }, [parentform.values, valuesByName, manifest]);
    const requiredValues = React.useMemo(() => {
        return manifest.filter((field) => field?.componentProps?.required).map(({ key }) => key);
    }, [valuesByName, manifest]);
    const componentError = React.useMemo(() => {
        if (errors !== null && typeof errors !== 'string' && Object.keys(errors)) {
            return commonTranslation('missingInformation', { ns: 'common' });
        } else if (typeof errors === 'string') {
            return t(errors);
        }
    }, [errors]);
    const validate = React.useCallback(() => {
        return props?.validator?.(undefined, valuesToValidate, requiredValues) ?? null;
    }, [props.validator, valuesToValidate, requiredValues]);
    React.useEffect(() => {
        if (hasRunValidation) {
            const validationResult = validate?.();
            setErrors(validationResult);
        }
    }, [validate, setErrors, hasRunValidation, parentform?.values]);

    return (
        <>
            <Input.Wrapper
                id={tableId}
                label={''}
                required={true}
                classNames={{
                    root: classes.root,
                    label: classes.label
                }}
                error={componentError ? <Box mt="md">{componentError}</Box> : null}
            >
                <InputValueTable
                    blueHeader
                    noHeader
                    // background={'#f9fcff'}
                    updateValue={updateValue}
                    validator={validate}
                    label={''}
                    data={tableData}
                    errors={typeof errors === 'object' ? errors : null}
                    validateOnChange={hasRunValidation}
                />
            </Input.Wrapper>
            <ActionButtons
                validator={validate}
                activeStep={props.activeStep}
                prevStep={props.parentPrev}
                nextStep={props.parentNext}
                setError={setErrors}
                setSubmitted={setHasRunValidation}
            />
        </>
    );
}
